import React from 'react';
import { PredictionBatch } from '../types';
import LottoBall from './LottoBall';
import { RefreshCw, Trophy } from 'lucide-react';

interface Props {
  batches: PredictionBatch[];
  loading: boolean;
  onRefresh: () => void;
}

const GeneratorListView: React.FC<Props> = ({ batches, loading, onRefresh }) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-white">Generator List</h1>
          <p className="text-slate-400 mt-1">Persistent history of every generated Lotto Plus number set, grouped by target draw.</p>
        </div>
        <button onClick={onRefresh} disabled={loading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 disabled:opacity-50">
          <RefreshCw size={17} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {!loading && batches.length === 0 && (
        <div className="border border-dashed border-slate-700 rounded-xl p-10 text-center text-slate-500">No generated number history yet. Generate numbers from the Dashboard and they will appear here.</div>
      )}

      {batches.map(batch => (
        <section key={batch.batchId} className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800 flex flex-wrap justify-between gap-3">
            <div>
              <div className="text-yellow-400 font-bold">Target Draw #{batch.targetDrawNumber}</div>
              <div className="text-xs text-slate-500">Target date {batch.targetDrawDate} • Generated {new Date(batch.createdAt).toLocaleString()}</div>
            </div>
            <div className={`text-xs px-3 py-1 rounded-full border ${batch.resultAvailable ? 'border-emerald-700 bg-emerald-950/40 text-emerald-300' : 'border-slate-700 bg-slate-950 text-slate-400'}`}>
              {batch.resultAvailable ? 'Official result checked' : 'Awaiting official result'}
            </div>
          </div>

          <div className="divide-y divide-slate-800/80">
            {batch.predictions.map((prediction, index) => {
              const mainMatches = prediction.matchedNumbers?.length || 0;
              const fullMain = Boolean(prediction.fullMainMatch || mainMatches === 5);
              const hasMatch = Boolean(prediction.resultChecked && (mainMatches > 0 || prediction.powerballMatch));
              return (
                <div key={`${batch.batchId}-${prediction.id}-${index}`} className={`p-5 ${fullMain ? 'bg-yellow-950/20' : hasMatch ? 'bg-emerald-950/10' : ''}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="font-semibold text-slate-200">{prediction.strategy}</div>
                    {prediction.resultChecked && (
                      <div className={`text-xs font-semibold flex items-center gap-1.5 ${fullMain ? 'text-yellow-300' : hasMatch ? 'text-emerald-300' : 'text-slate-500'}`}>
                        {(fullMain || hasMatch) && <Trophy size={14} />}
                        {fullMain ? `Winner — 5/5${prediction.powerballMatch ? ' + Power Ball' : ''}` : `${mainMatches}/5${prediction.powerballMatch ? ' + Power Ball' : ''}`}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {prediction.numbers.map((n, i) => <LottoBall key={i} number={n} size="sm" />)}
                    <span className="text-slate-600 mx-1">+</span>
                    <LottoBall number={prediction.powerball} powerball size="sm" title="Power Ball" />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default GeneratorListView;
